import { ConstructionOutlined } from '@mui/icons-material'
import { CustomDefinition, CustomMetricSource, CustomMetricValueType, PartyRole } from '../../interfaces/db'

export const COLORS = {
  primary: {
    light: '#e3f2fd',
    main: '#2196f3',
    dark: '#1976d2',
    gradient: 'linear-gradient(45deg, #1976d2, #2196f3)',
    hover: '#1565c0'
  },
  secondary: {
    light: '#f3e5f5',
    main: '#9c27b0',
    dark: '#7b1fa2',
    gradient: 'linear-gradient(45deg, #7b1fa2, #9c27b0)'
  },
  success: {
    light: '#e8f5e9',
    main: '#4caf50',
    gradient: 'linear-gradient(45deg, #388e3c, #4caf50)'
  },
  background: {
    gradient: 'linear-gradient(to bottom right, #f5f9ff 0%, #ffffff 100%)',
    card: '#ffffff',
    hover: 'rgba(33, 150, 243, 0.04)'
  }
}

export const defaultDefinition: CustomDefinition = {
  custom_definition_id: '',
  team_id: '',
  metric_idx: null,
  label: '',
  description: '',
  order: 0,
  tags: null,
  value_source: CustomMetricSource.GEN_AI,
  value_type: CustomMetricValueType.PERCENT,
  value_type_enum_str: null,
  value_gen_str: null,
  value_gen_desc_str: null,
  value_phrase_str: null,
  value_phrase_role: null,
  value_average_str: null,
  value_code_str: null,
  where_min_duration: null,
  where_max_duration: null,
  where_gen_str: null,
  modified_as_of: null,
  rebuilt_as_of: null,
} 

export const formatMetricSource = (source: CustomMetricSource): string => {
  const formats: Record<CustomMetricSource, string> = {
    [CustomMetricSource.GEN_AI]: 'Generative AI',
    [CustomMetricSource.PHRASE]: 'Phrase Match',
    [CustomMetricSource.AVERAGE]: 'Average Score',
    [CustomMetricSource.CODE]: 'Custom Code'
  }
  return formats[source]
}

export const formatMetricType = (type: CustomMetricValueType): string => {
  const formats: Record<CustomMetricValueType, string> = {
    [CustomMetricValueType.BOOLEAN]: 'Yes/No',
    [CustomMetricValueType.PERCENT]: 'Percentage',
    [CustomMetricValueType.TEXT]: 'Text',
    [CustomMetricValueType.ENUM]: 'Multiple Choice'
  }
  return formats[type]
}

export function validateCustomDefinition(def: CustomDefinition): { valid: boolean; reason: string } {
  // Basic validation
  if (!def.label) {
    return { valid: false, reason: 'Label is required' };
  }
  if (def.label.includes(',')) {
    return { valid: false, reason: 'Label cannot contain commas' };
  }

  // Source-specific validation
  switch (def.value_source) {
    case CustomMetricSource.GEN_AI: {
      if (!def.description && def.value_source === CustomMetricSource.GEN_AI) {
        return { valid: false, reason: 'Description is required for AI-based metrics' };
      }

      // Validate descriptions based on value type
      const descriptions = decodeDescriptions(def);
      
      switch (def.value_type) {
        case CustomMetricValueType.BOOLEAN: {
          const yesDesc = descriptions.find(d => d.category === 'yes')?.description;
          const noDesc = descriptions.find(d => d.category === 'no')?.description;
          if (!yesDesc || !noDesc) {
            return { valid: false, reason: 'Both Yes and No descriptions are required' };
          }
          break;
        }
        case CustomMetricValueType.PERCENT: {
          const lowDesc = descriptions.find(d => d.category === 'Low (<40%)')?.description;
          const midDesc = descriptions.find(d => d.category === 'Middle (40-70%)')?.description;
          const highDesc = descriptions.find(d => d.category === 'High (>70%)')?.description;
          if (!lowDesc || !midDesc || !highDesc) {
            return { valid: false, reason: 'All percentage range descriptions are required' };
          }
          break;
        }
        case CustomMetricValueType.ENUM: {
          if (!def.value_type_enum_str || def.value_type_enum_str.split(',').some(c => !c.trim())) {
            return { valid: false, reason: 'All category names must be non-empty' };
          }
          const categories = def.value_type_enum_str.split(',').map(c => c.trim());
          const missingDescriptions = categories.filter(cat => 
            !descriptions.find(d => d.category === cat)?.description
          );
          if (missingDescriptions.length > 0) {
            return { valid: false, reason: `Missing descriptions for categories: ${missingDescriptions.join(', ')}` };
          }
          break;
        }
      }
      break;
    }
    case CustomMetricSource.PHRASE: {
      if (!def.value_phrase_str) {
        return { valid: false, reason: 'Search phrase is required' };
      }
      break;
    }
    case CustomMetricSource.AVERAGE: {
      if (!def.value_average_str) {
        return { valid: false, reason: 'Metrics to average are required' };
      }
      break;
    }
    case CustomMetricSource.CODE: {
      if (!def.value_code_str) {
        return { valid: false, reason: 'Code selection is required' };
      }
      break;
    }
  }

  return { valid: true, reason: '' };
}

export const defaultMetrics = {
  qa: [
    {
      label: 'Customer Satisfaction',
      description: 'Evaluates overall customer satisfaction based on the conversation',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Evaluates overall customer satisfaction based on the conversation',
      value_gen_desc_str: '0:Complete dissatisfaction with service and resolution,50:Mixed or neutral satisfaction with partial resolution,100:Complete satisfaction with excellent service and resolution'
    },
    {
      label: 'Problem Resolution',
      description: 'Determines if the customer\'s issue was fully resolved',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.BOOLEAN,
      value_gen_str: 'Determines if the customer\'s issue was fully resolved',
      value_gen_desc_str: 'yes:The customer\'s issue was completely resolved and they acknowledged the resolution,no:The issue remains unresolved or requires further assistance'
    },
    {
      label: 'Agent Professionalism',
      description: 'Assesses the agent\'s level of professionalism and courtesy',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.ENUM,
      value_type_enum_str: 'Excellent, Good, Fair, Poor',
      value_gen_str: 'Assesses the agent\'s level of professionalism and courtesy',
      value_gen_desc_str: 'Excellent:Consistently professional, courteous, and goes above and beyond,Good:Generally professional and courteous throughout the interaction,Fair:Basic level of professionalism with room for improvement,Poor:Lacks professionalism or displays inappropriate behavior'
    },
    {
      label: 'First Contact Resolution',
      description: 'Evaluates if the issue was resolved in the first interaction',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.BOOLEAN,
      value_gen_str: 'Evaluates if the issue was resolved in the first interaction',
      value_gen_desc_str: 'yes:Issue was completely resolved within this single interaction without need for follow-up,no:Issue requires additional interactions or escalation to resolve'
    },
    {
      label: 'Technical Knowledge',
      description: 'Measures the agent\'s technical expertise and ability to explain complex concepts',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Measures the agent\'s technical expertise and ability to explain complex concepts',
      value_gen_desc_str: 'Low (<40%):Demonstrates poor technical knowledge and inability to explain concepts,Middle (40-70%):Shows basic technical understanding but struggles with complex issues,High (>70%):Exhibits excellent technical expertise and clear explanations'
    }
  ],
  sales: [
    {
      label: 'Opening Quality',
      description: 'Evaluates the effectiveness of the sales rep\'s opening approach in cold calls',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Evaluates the effectiveness of the sales rep\'s opening approach in cold calls',
      value_gen_desc_str: 'Low (<40%):Poor opener lacking clarity and engagement,Middle (40-70%):Clear opener but lacks personalization or research,High (>70%):Excellent opener with strong personalization showing thorough research'
    },
    {
      label: 'Objection Handling Quality',
      description: 'Assesses how effectively the sales rep handles and overcomes objections',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Assesses how effectively the sales rep handles and overcomes objections',
      value_gen_desc_str: 'Low (<40%):No attempt to address or overcome objections,Middle (40-70%):Attempted to handle objections but responses were ineffective,High (>70%):Successfully identified and overcame multiple objections'
    },
    {
      label: 'Qualification Quality',
      description: 'Measures the thoroughness of prospect qualification and pain point discovery',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Measures the thoroughness of prospect qualification and pain point discovery',
      value_gen_desc_str: 'Low (<40%):No qualification questions or pain discovery attempted,Middle (40-70%):Basic qualification with 1-2 questions but insufficient exploration,High (>70%):Comprehensive qualification with thorough pain point discovery'
    },
    {
      label: 'Closing Quality',
      description: 'Evaluates the effectiveness of meeting scheduling and commitment securing',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.PERCENT,
      value_gen_str: 'Evaluates the effectiveness of meeting scheduling and commitment securing',
      value_gen_desc_str: 'Low (<40%):Failed to secure any meeting or next steps,Middle (40-70%):Secured a tentative meeting without strong commitment,High (>70%):Successfully booked a meeting with clear next steps'
    },
    {
      label: 'Overcame Objection',
      description: 'Determines if the sales rep successfully overcame a prospect objection',
      value_source: CustomMetricSource.GEN_AI,
      value_type: CustomMetricValueType.BOOLEAN,
      value_gen_str: 'Determines if the sales rep successfully overcame a prospect objection',
      value_gen_desc_str: 'yes:Successfully overcame at least one significant objection with an effective response,no:Failed to overcome objections or did not address them effectively'
    }
  ]
}

export const isDefinitionValid = (def: CustomDefinition): { valid: boolean, reason: string } => {
  // Basic validation for all metrics
  if (!def.label?.trim()) {
    return { valid: false, reason: 'Label is required' }
  }
  if (!def.value_source) {
    return { valid: false, reason: 'Value source is required' }
  }
  if (!def.value_type) {
    return { valid: false, reason: 'Value type is required' }
  }

  // Description is required for all sources except CODE
  if (def.value_source !== CustomMetricSource.CODE && !def.description?.trim()) {
    return { valid: false, reason: 'Description is required' }
  }

  // Source-specific validation
  switch (def.value_source) {
    case CustomMetricSource.CODE:
      if (!def.value_code_str) {
        return { valid: false, reason: 'Please select a code metric' }
      }
      break;

    case CustomMetricSource.GEN_AI:
      if (!def.value_gen_str?.trim() && !def.value_gen_desc_str?.trim()) {
        return { valid: false, reason: 'Generation prompt or description is required' }
      }
      break;

    case CustomMetricSource.PHRASE:
      if (!def.value_phrase_str?.trim()) {
        return { valid: false, reason: 'Phrase is required' }
      }
      break;

    case CustomMetricSource.AVERAGE:
      if (!def.value_average_str?.trim()) {
        return { valid: false, reason: 'Metrics to average are required' }
      }
      break;
  }

  // Type-specific validation
  switch (def.value_type) {
    case CustomMetricValueType.ENUM:
      if (def.value_source !== CustomMetricSource.CODE && !def.value_type_enum_str?.trim()) {
        return { valid: false, reason: 'Categories are required for enum type' }
      }
      break;
  }

  return { valid: true, reason: '' }
}

// Change the delimiter to something that won't appear in normal text
export interface DescriptionPair {
  category: string;
  description: string;
}

export function encodeDescriptions(descriptions: DescriptionPair[]): string {
  return descriptions.map(d => `${d.category}:${d.description}`).join(' ');
}

export function decodeDescriptions(def: CustomDefinition): DescriptionPair[] {
  if (!def.value_gen_desc_str) return [];
  switch (def.value_type) {
    case CustomMetricValueType.BOOLEAN:
      const yes = def.value_gen_desc_str?.split('yes:')[1].split('no:')[0]
      const no = def.value_gen_desc_str?.split('no:')[1]
      return [
        { category: 'yes', description: yes || '' },
        { category: 'no', description: no || '' }
      ]
    case CustomMetricValueType.PERCENT:
      // split between Low, Middle, 
      // split by Low (<40%): 
      // split by Middle (40-70%):
      // split by High (>70
      // text is of the form "Low (<40%): <description>, Middle (40-70%): <description>, High (>70%): <description>"
      const lowSplit = def.value_gen_desc_str?.split('Low (<40%):')
      const low = lowSplit && lowSplit.length > 1 ? lowSplit[1]?.split('Middle (40-70%):')[0] : ''
      
      const midSplit = def.value_gen_desc_str?.split('Middle (40-70%):')
      const mid = midSplit && midSplit.length > 1 ? midSplit[1]?.split('High (>70%):')[0] : ''
      
      const highSplit = def.value_gen_desc_str?.split('High (>70%):')
      const high = highSplit && highSplit.length > 1 ? highSplit[1] : ''
      
      return [
        { category: 'Low (<40%)', description: low || '' },
        { category: 'Middle (40-70%)', description: mid || '' },
        { category: 'High (>70%)', description: high || '' }
      ]
    case CustomMetricValueType.ENUM:
      // split between categories
      // try to split by category delimeter otherwise split by comma
      const categories = def.value_type_enum_str?.split(',').map(c => c.trim()) || []
      const pair: DescriptionPair[] = []
      for (let i = 0; i < categories.length; i++) {
        const cat = categories[i]
        const desc = def.value_gen_desc_str?.split(`${cat}:`)[1]
        if (i < categories.length - 1) {
          const nextCat = categories[i + 1]
          const nextDesc = desc.split(`${nextCat}:`)[0]
          pair.push({ category: cat, description: nextDesc || '' })
        } else {
          pair.push({ category: cat, description: desc || '' })
        }
      }
      return pair
    default:
      return [];
  }
}

export function formatDescriptionForValueType(
  def: CustomDefinition,
  descriptions: DescriptionPair[]
): string {
  switch (def.value_type) {
    case CustomMetricValueType.BOOLEAN: {
      const yesDesc = descriptions.find(d => d.category === 'yes')?.description || '';
      const noDesc = descriptions.find(d => d.category === 'no')?.description || '';
      return encodeDescriptions([
        { category: 'yes', description: yesDesc },
        { category: 'no', description: noDesc }
      ]);
    }
    case CustomMetricValueType.PERCENT: {
      const lowDesc = descriptions.find(d => d.category === 'Low (<40%)')?.description || '';
      const midDesc = descriptions.find(d => d.category === 'Middle (40-70%)')?.description || '';
      const highDesc = descriptions.find(d => d.category === 'High (>70%)')?.description || '';
      return encodeDescriptions([
        { category: 'Low (<40%)', description: lowDesc },
        { category: 'Middle (40-70%)', description: midDesc },
        { category: 'High (>70%)', description: highDesc }
      ]);
    }
    case CustomMetricValueType.ENUM: {
      if (!def.value_type_enum_str) return '';
      const categories = def.value_type_enum_str.split(',').map(c => c.trim());
      return encodeDescriptions(
        categories.map(cat => ({
          category: cat,
          description: descriptions.find(d => d.category === cat)?.description || ''
        }))
      );
    }
    default:
      return '';
  }
} 